import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { clearOrgScopedStorage, clearSession, getSessionToken, setActiveOrgId, setDefaultWatchlistId } from '@/auth/session'
import { clientApi, MeResp } from '@/api/client'
import { qk } from '@/api/queryKeys'

function NavLink({ to, label, badge, matchPaths }: { to: string; label: string; badge?: number | null; matchPaths?: string[] }) {
  const loc = useLocation()
  const pathsToMatch = matchPaths && matchPaths.length > 0 ? matchPaths : [to]
  const active = pathsToMatch.some(
    (p) => loc.pathname === p || (p !== '/' && loc.pathname.startsWith(p)),
  )
  return (
    <Link className={active ? 'navlink active' : 'navlink'} to={to}>
      {label}
      {badge && badge > 0 ? (
        <span className="pill ok" style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px' }}>{badge}</span>
      ) : null}
    </Link>
  )
}

export default function Topbar({ subtitle, right }: { subtitle?: string; right?: React.ReactNode }) {
  const nav = useNavigate()
  const authed = !!getSessionToken()

  const [switchOpen, setSwitchOpen] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const [switching, setSwitching] = useState(false)

  const debounceRef = useRef<number | null>(null)

  // Secondary-nav "More" menu: keep Workspaces as the one primary section and
  // tuck My Questions / Universe behind a dropdown so a new user isn't faced
  // with several equal-weight choices.
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!moreOpen) return
    const onDoc = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [moreOpen])

  // Session / `me` via React Query.
  const meQuery = useQuery<MeResp>({
    queryKey: qk.me(),
    queryFn: () => clientApi.me(),
    enabled: authed,
    staleTime: 30_000,
  })
  const me = meQuery.data ?? null
  const meErr = meQuery.error ? (meQuery.error as any)?.message || 'Failed to load session' : null

  useEffect(() => {
    const oid = String(me?.active_org_id || '').trim()
    if (oid) setActiveOrgId(oid)
  }, [me?.active_org_id])

  const isStaff = !!me?.is_cenva_staff
  const activeOrgLabel = useMemo(() => {
    const name = me?.active_org?.name
    const slug = me?.active_org?.slug
    if (name && slug) return `${name} (${slug})`
    if (name) return String(name)
    if (me?.active_org_id) return `org ${me.active_org_id.slice(0, 10)}`
    return 'org'
  }, [me])

  useEffect(() => {
    if (!switchOpen) return
    const query = q.trim()
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      setSearching(true)
      clientApi.orgSearch(query, 30)
        .then((r) => setResults(Array.isArray(r?.orgs) ? r.orgs : []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false))
    }, 220)
  }, [q, switchOpen])

  // Page-level ESC support for the org switcher modal. Clicking the overlay
  // already closes it, but keyboard users need a direct path that doesn't
  // depend on the modal having focus.
  useEffect(() => {
    if (!switchOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSwitchOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [switchOpen])

  // Running-ask badge: shared query key with MyQuestions + AskInput, so all
  // three views re-use the same cached `/v1/jobs?ask_only=true` fetch.
  const jobsQuery = useQuery({
    queryKey: qk.jobs.list({ askOnly: true, limit: 30 }),
    queryFn: () => clientApi.jobs(undefined, 30, true),
    enabled: authed,
    // Polls every 15s while tab is visible; paused when hidden via the
    // QueryClient default `refetchIntervalInBackground: false`.
    refetchInterval: 15_000,
    staleTime: 5_000,
  })
  const runningAskCount = useMemo(() => {
    const jobs = Array.isArray(jobsQuery.data?.jobs) ? jobsQuery.data!.jobs : []
    return jobs.filter((j) => j.status === 'queued' || j.status === 'running').length
  }, [jobsQuery.data])

  async function doSwitch(orgId: string) {
    const oid = String(orgId || '').trim()
    if (!oid) return
    setSwitching(true)
    try {
      const res = await clientApi.setActiveOrg(oid)
      // Drop any org-scoped UI state from the previous org before the hard
      // refresh, so the ask queue / drafts don't bleed across orgs.
      clearOrgScopedStorage()
      setActiveOrgId(oid)
      if (res?.default_watchlist_id) setDefaultWatchlistId(res.default_watchlist_id)
      setSwitchOpen(false)
      // Hard refresh to reset route-local state.
      window.location.assign('/dashboard')
    } catch (e: any) {
      alert(e?.message || 'Failed to switch org')
    } finally {
      setSwitching(false)
    }
  }

  function logout() {
    // Fire-and-forget the server-side logout so the HttpOnly cookie is
    // cleared. Even if the request fails (e.g. already logged out),
    // we still drop the local session so the user ends up on /login.
    void clientApi.authLogout().catch(() => null)
    clearSession()
    nav('/login')
  }

  return (
    <div className="topbar">
      <Link className="brand" to={authed ? '/workspaces' : '/login'} aria-label="Cenva Intelligence — Workspaces home">
        <div className="brandMark" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 4 Q13 9 26 18" stroke="rgba(255,255,255,0.30)" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M3 12 Q13 14 26 18" stroke="rgba(255,255,255,0.55)" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M3 24 Q13 22 26 18" stroke="rgba(255,255,255,0.55)" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M3 32 Q13 27 26 18" stroke="rgba(255,255,255,0.30)" strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="28" cy="18" r="5" fill="rgba(255,255,255,0.07)"/>
            <circle cx="28" cy="18" r="2.8" fill="rgba(255,255,255,0.90)"/>
          </svg>
        </div>
        <div>
          <div className="brandName">Cenva Intelligence</div>
          <div className="sub">{subtitle || 'Insight dashboard'}</div>
        </div>
      </Link>

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        {authed ? (
          <div className="nav">
            <NavLink
              to="/workspaces"
              label="Workspaces"
              matchPaths={['/workspaces', '/dashboard']}
            />
            <div ref={moreRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className={moreOpen ? 'navlink active' : 'navlink'}
                onClick={() => setMoreOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={moreOpen}
              >
                More ▾
                {runningAskCount && runningAskCount > 0 ? (
                  <span className="pill ok" style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px' }}>{runningAskCount}</span>
                ) : null}
              </button>
              {moreOpen ? (
                <div className="card" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, padding: 6, minWidth: 190, zIndex: 50 }}>
                  <Link className="navlink" to="/my-questions" onClick={() => setMoreOpen(false)} style={{ display: 'block', padding: '8px 10px' }}>
                    My Questions{runningAskCount && runningAskCount > 0 ? ` (${runningAskCount})` : ''}
                  </Link>
                  <Link className="navlink" to="/universe" onClick={() => setMoreOpen(false)} style={{ display: 'block', padding: '8px 10px' }}>
                    Universe
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {authed && isStaff ? (
          <div className="row" style={{ gap: 6 }}>
            <NavLink to="/packs" label="Packs" />
            <span className="pill" title={me?.cenva_access_mode ? `mode: ${me.cenva_access_mode}` : ''}>
              Org: {activeOrgLabel}
            </span>
            <button className="btn" onClick={() => setSwitchOpen(true)} disabled={switching}>
              Switch org
            </button>
          </div>
        ) : null}

        {/* Ask usage meter — compact summary of the ask quotas exposed by
            /v1/me. Primary figure is per-account for the signed-in user,
            falls back to org totals. */}
        {authed && me?.ask_usage && (() => {
          const acct = me.ask_usage?.account
          const org = me.ask_usage?.org
          const useAccount = !!acct
          const running = useAccount ? (acct?.running ?? 0) : (org?.running ?? 0)
          const used = useAccount ? (acct?.last_hour ?? 0) : (org?.last_hour ?? 0)
          const max = useAccount ? (acct?.max_per_hour ?? 0) : (org?.max_per_hour ?? 0)
          const nearLimit = max > 0 && used >= Math.max(1, Math.floor(max * 0.8))
          const atLimit = max > 0 && used >= max
          const title = [
            `running: ${running}`,
            max > 0 ? `this hour: ${used}/${max}${useAccount ? ' (you)' : ' (org)'}` : `this hour: ${used}`,
            org && org.max_concurrent > 0 ? `concurrent cap: ${org.max_concurrent}` : null,
            useAccount && org ? `org this hour: ${org.last_hour}${org.max_per_hour ? `/${org.max_per_hour}` : ''}` : null,
          ].filter(Boolean).join(' · ')
          return (
            <span
              className={`pill ${atLimit ? 'bad' : nearLimit ? 'warn' : ''}`}
              title={title}
              aria-label={`Ask usage — ${title}`}
              style={{ fontSize: 11 }}
            >
              {running} running
              {max > 0 ? <> · {used}/{max} asks this hr</> : null}
            </span>
          )
        })()}

        {right || null}

        {authed ? (
          <button className="btn" onClick={logout}>
            Sign out
          </button>
        ) : null}
      </div>

      {switchOpen && (
        <div
          className="modalOverlay"
          onClick={() => setSwitchOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSwitchOpen(false)
          }}
          role="presentation"
        >
          <div
            className="modalCard"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="switch-org-title"
            tabIndex={-1}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div id="switch-org-title" style={{ fontWeight: 800 }}>Switch Org</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  Cenva staff access. Current: {activeOrgLabel}
                </div>
              </div>
              <button className="btn" onClick={() => setSwitchOpen(false)} aria-label="Close switch org dialog">
                Close
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <input
                className="input"
                placeholder="Search orgs by name or slug"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
              />
              {meErr && <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>{meErr}</div>}
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                {searching ? 'Searching…' : `${results.length} result${results.length === 1 ? '' : 's'}`}
              </div>
            </div>

            <div style={{ marginTop: 10, maxHeight: 280, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
              {results.length === 0 ? (
                <div className="muted" style={{ padding: 12, fontSize: 12 }}>
                  No orgs found.
                </div>
              ) : (
                results.map((o: any) => (
                  <button
                    key={o?.org_id}
                    className="orgRow"
                    onClick={() => doSwitch(String(o?.org_id || ''))}
                    disabled={switching}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {o?.name || o?.slug || o?.org_id}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {o?.slug ? `slug: ${o.slug}` : ''}{o?.plan ? ` · plan: ${o.plan}` : ''}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
